/**
 * Authentication hooks for Admin Portal
 * Similar pattern to interface project but adapted for React app with localStorage
 */

import { useState, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { apiClient } from '../lib/api/api-client';
import { useAuthStore, type AuthTokens, type User } from '../store/authStore';

// Types
interface LoginCredentials {
  email: string;
  password: string;
}

interface LoginFormData {
  email: string;
  password: string;
}

interface UseLoginFormProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

interface ApiResponse<T = any> {
  data: T;
  status: number;
}

interface GotikApiResponse<T = any> {
  status: {
    code: string;
    message: string;
  };
  data: T;
  metaData: {
    requestId: string;
    timestamp: number;
  };
}

// Login Hook
export const useLoginForm = ({ onSuccess, onError }: UseLoginFormProps = {}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { setAuthTokens, setUser } = useAuthStore();

  const formMethods = useForm<LoginFormData>({
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onChange',
  });

  const onSubmit = useCallback(async (data: LoginFormData) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.login(data.email, data.password) as ApiResponse<GotikApiResponse<AuthTokens>>;
      
      if (response.data?.data) {
        const authTokens = response.data.data;
        
        // Store tokens in auth store
        setAuthTokens(authTokens);
        
        // Parse user data from tokens or response
        // In a real implementation, you'd decode the JWT or make a user profile call
        const userData: User = {
          id: authTokens.idToken || 'admin',
          email: data.email,
          role: 'ADMIN',
          emailVerified: true,
        };
        
        setUser(userData);
        
        onSuccess?.();
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [setAuthTokens, setUser, onSuccess, onError]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    formMethods,
    onSubmit,
    isLoading,
    error,
    clearError,
  };
};

// Logout Hook
export const useLogout = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { logout } = useAuthStore();

  const handleLogout = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Call API logout first (if possible)
      try {
        await apiClient.logout();
      } catch (error) {
        console.error('API logout error:', error);
        // Continue with local cleanup even if API call fails
      }
      
      // Clear local auth state
      logout();
      
      // Small delay to ensure state cleanup is complete
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Force redirect to login page
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
      // Force logout and redirect even if there are errors
      logout();
      window.location.href = '/login';
    } finally {
      setIsLoading(false);
    }
  }, [logout]);

  return {
    logout: handleLogout,
    isLoading,
  };
};

// Token refresh hook
export const useTokenRefresh = () => {
  const { setAuthTokens, logout } = useAuthStore();

  const refreshToken = useCallback(async () => {
    try {
      const response = await apiClient.refreshToken() as ApiResponse<GotikApiResponse<AuthTokens>>;
      
      if (response.data?.data) {
        setAuthTokens(response.data.data);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
      return false;
    }
  }, [setAuthTokens, logout]);

  return { refreshToken };
};

// Auth verification hook
export const useAuthVerification = () => {
  const { setUser, logout } = useAuthStore();

  const verifyAuth = useCallback(async () => {
    try {
      const response = await apiClient.verifyToken() as ApiResponse<GotikApiResponse<any>>;
      
      if (response.data?.data) {
        const verificationResult = response.data.data;
        
        if (verificationResult.status === 'VALID') {
          // Update user data with verified information
          const userData: User = {
            id: verificationResult.username || 'admin',
            email: verificationResult.username || '',
            role: (verificationResult.roles?.[0] || 'ADMIN') as 'ADMIN' | 'OPERATOR' | 'USER',
            emailVerified: true,
          };
          
          setUser(userData);
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('Auth verification failed:', error);
      logout();
      return false;
    }
  }, [setUser, logout]);

  return useMemo(() => ({ verifyAuth }), [verifyAuth]);
};
